import { approval } from 'reducers/common/dictionaries';
import {
	isAdstudioCreative,
	isSocialCreative,
	hasPermission,
	isVASTCreative,
	isDynamic,
	isVASTVideoCreative,
	isDisplayCreative,
	isNativeImageCreative,
	transcodingChecks,
	isIncomplete,
	isImposiumCreative,
	isCtvCreative
} from 'utils/common';
import {
	 CV,
	 CREATIVE_VIEW,
	 SOCIAL_AD_TYPES,
	 STATUS_MAP,
	 PLATFORM_BY_ADTYPE,
	 PREROLL,
	 DYNAMIC_PREROLL,
	 PARTNER_VAST,
	 DISPLAY_HTML_TAG,
	 VAST_YOUTUBE
} from "utils/constants";
import { getDownloadAS, getPartnerTags } from "utils/Flags";

/**
 * @param {Object} dependentValues
 * @param {string} dependentValues.adType
 * @param {Set<string>} allowedPlatforms
 * @returns {boolean}
 */
export function isCorrectPlatform(dependentValues, allowedPlatforms) {
	const { adType } = dependentValues;
	const platform = PLATFORM_BY_ADTYPE[adType];
	return allowedPlatforms.has(platform);
}

export const isDVAST = (adType) => {
	return (adType === 'dynamic_preroll') || (adType === 'dynamicPreRoll');
};

export function isPartnerVAST(adType){
	return adType === PARTNER_VAST;
}

export function isYoutubeVAST(adType){
	return adType === VAST_YOUTUBE;
}

export function isInvalidDVASTState(dependentValues = {}) {
	if(!isDVAST(dependentValues.adType)){
		return false;
	}

	const isTranscoding = transcodingChecks.transcoding(dependentValues);
	const hasTranscodingError = transcodingChecks.error(dependentValues);
	const isIncompleteStatus = isIncomplete(dependentValues);

	return [
		isTranscoding,
		hasTranscodingError,
		isIncompleteStatus
	].some(item => item);
}

export function isYouTube(adType){
	return [SOCIAL_AD_TYPES.YOUTUBE_DISCOVERY, SOCIAL_AD_TYPES.YOUTUBE_INSTREAM].includes(adType);
}

export function isConcept(dependentValues) {
	return Boolean(dependentValues?.conceptId);
}

const compareStates = (apiState, uiState) => approval.apiToUI[apiState] === uiState;

export const isProduction = apiState => compareStates(apiState, STATUS_MAP.unsubmitted.label);
export const isProductionIncomplete = apiState => compareStates(apiState, STATUS_MAP.production_incomplete.label);
export const isApproving = apiState => compareStates(apiState, STATUS_MAP.pending_approval.label);
export const isApproved = apiState => compareStates(apiState, STATUS_MAP.approved.label);
export const isRejected = apiState => compareStates(apiState, STATUS_MAP.failed.label);
export const isRejectedIncomplete = apiState => compareStates(apiState, STATUS_MAP.rejected_incomplete.label);
export const isQA = apiState => compareStates(apiState, STATUS_MAP.submitted.label);
export const isTranscoding = apiState => compareStates(apiState, STATUS_MAP.transcoding.label);
export const isTranscodingError = apiState => compareStates(apiState, STATUS_MAP.transcoding_error.label);

export function isPartnerRejected(apiState){
	return compareStates(apiState, STATUS_MAP.partner_rejected.label);
}

/**
 * Checks if the creative can have a closed caption file
 * @param {?Object} dependentValues - Creative data
 * @returns {boolean}
 */
export const canHaveClosedCaption = dependentValues => (
	dependentValues?.adType === PREROLL
);

/**
 * Checks if the creative has a closed caption file
 * @param {?Object} dependentValues - Creative data
 * @returns {boolean}
 */
export const hasClosedCaption = dependentValues => (
	dependentValues?.closedCaptions?.length > 0
);

export const approvalBooleans = {
	compareStates,
	isProduction,
	isProductionIncomplete,
	isApproving,
	isApproved,
	isRejected,
	isRejectedIncomplete,
	isQA,
	isTranscoding,
	isTranscodingError,
	isPartnerRejected,
};

export const shouldDisplayAction = {
	submit(user, dependentValues) {
		let { approval } = dependentValues
			, isCorrectState = (isProduction(approval) || isRejected(approval) || isPartnerRejected(approval))
			;

		return hasPermission(user, CV, CREATIVE_VIEW.SUBMIT_ADS_TO_QA) && isCorrectState;
	},
	approve(user, dependentValues) {
		let { approval } = dependentValues
			, isCorrectState = (isProduction(approval) || isRejected(approval) || isQA(approval) || isPartnerRejected(approval))
			;

		if(isSocialCreative(dependentValues)){
			return hasPermission(user, CV, CREATIVE_VIEW.APPROVE_SOCIAL) && isCorrectState;
		}

		return hasPermission(user, CV, CREATIVE_VIEW.APPROVE_ADS) && isCorrectState;
	},
	approveDVAST(user, dependentValues) {
		let { approval } = dependentValues
			, isCorrectState = (isProduction(approval) || isRejected(approval) || isQA(approval) || isPartnerRejected(approval))
			;

		// Additional permissions required for DVAST creatives
		return hasPermission(user, CV, CREATIVE_VIEW.APPROVE_DVAST_CREATIVES) && isCorrectState;
	},
	recall(user, dependentValues) {
		let { approval } = dependentValues
			, isCorrectState = (isQA(approval) || isRejected(approval) || isPartnerRejected(approval))
			;

		return hasPermission(user, CV, CREATIVE_VIEW.RECALL_ADS) && isCorrectState;
	},
	reject(user, dependentValues) {
		let { approval } = dependentValues
			, isImposiumTranscodingError = isImposiumCreative(dependentValues) && isTranscodingError(approval)
			, isCorrectState = (isProduction(approval) || isQA(approval) || isApproving(approval) || isApproved(approval) || isImposiumTranscodingError)
			;

		if(isSocialCreative(dependentValues)){
			return hasPermission(user, CV, CREATIVE_VIEW.APPROVE_SOCIAL) && isCorrectState;
		}

		return hasPermission(user, CV, CREATIVE_VIEW.REJECT_ADS) && isCorrectState;
	},
	rejectionReason(user, dependentValues){
		return isRejected(dependentValues.approval);
	},
	download(user, dependentValues = false){
		const permissionCheck = hasPermission(user, CV, CREATIVE_VIEW.DOWNLOAD);
		const { adType } = dependentValues || {};

		if(isSocialCreative(dependentValues)){
			return false;
		}

		if(isVASTCreative(dependentValues)){
			return false;
		}

		if (isNativeImageCreative(dependentValues)) {
			return false;
		}

		if(isCtvCreative(dependentValues)){
			return false;
		}

		if(adType === PARTNER_VAST || adType === DISPLAY_HTML_TAG || adType === VAST_YOUTUBE){
			return false;
		}

		if(!permissionCheck){
			return false;
		}

		// HOPI-1303
		if(isAdstudioCreative(dependentValues)){
			return getDownloadAS();
		}

		return true;
	},
	encodeView(dependentValues){
		let { videos, adType } = dependentValues;

		return (videos && videos.length) && (adType !== PREROLL && adType !== DYNAMIC_PREROLL);
	},
	creativePreview(dependentValues){
		if(isCtvCreative(dependentValues)){ return false; }
		let { approval } = dependentValues;
		return !isProductionIncomplete(approval) && !isRejectedIncomplete(approval) && !isInvalidDVASTState(dependentValues);
	},
	createMock(user){
		return hasPermission(user, CV, CREATIVE_VIEW.CREATE_MOCK_UP_DISPLAY);
	},
	deleteConcept(user, dependentValues) {
		let permissionCheck = hasPermission(user, CV, CREATIVE_VIEW.DELETE);

		if(!permissionCheck){return false;}
		if(!dependentValues){ return permissionCheck; }

		let { approval } = dependentValues;
		let creativeCheck = isConcept(dependentValues) && (isProduction(approval) || isProductionIncomplete(approval));

		return creativeCheck && permissionCheck;
	},
	deleteCreative(user, dependentValues = false){
		const permissionCheck = hasPermission(user, CV, CREATIVE_VIEW.DELETE);

		if(!dependentValues) {
			return permissionCheck;
		}
		const { approval } = dependentValues;
		const isCorrectState = [
			isProduction,
			isProductionIncomplete,
			isRejected,
			isRejectedIncomplete,
			isTranscodingError,
		].some(statusCheck => statusCheck(approval));

		return permissionCheck && isCorrectState;
	},
	renameCreative(user)  {
		return hasPermission(user, CV, CREATIVE_VIEW.RENAME);
	},
	renameCreativeFilename(user, dependentValues)  {
		let { approval } = dependentValues;
		let isCorrectState = (isRejected(approval) || isRejectedIncomplete(approval) || isProductionIncomplete(approval) || isProduction(approval));

		if(isSocialCreative(dependentValues)) {return false;}
		if(isCtvCreative(dependentValues)) {return false;}

		return hasPermission(user, CV, CREATIVE_VIEW.RENAME) && isCorrectState;
	},
	duplicateCreative(user, dependentValues){
		const permissionCheck = hasPermission(user, CV, CREATIVE_VIEW.DUPLICATE)
			, isCorrectState = !(isAdstudioCreative(dependentValues) || isSocialCreative(dependentValues) || isConcept(dependentValues) || isCtvCreative(dependentValues) || dependentValues?.adType === VAST_YOUTUBE);

		if(!dependentValues){
			return permissionCheck;
		}

		if (dependentValues.isBulk) {
			return permissionCheck&&!dependentValues.selectedItems.some(item => item.conceptId);
		}

		return permissionCheck && isCorrectState;
	},
	selectNewVideo(user, dependentValues){
		if (!isVASTVideoCreative(dependentValues)) { return false; }

		const permissionCheck = hasPermission(user, CV, CREATIVE_VIEW.UPLOAD_VAST);

		if(!dependentValues) {
			return permissionCheck;
		}

		const isCorrectState = isTranscodingError(dependentValues.approval);

		return permissionCheck && isCorrectState;
	},
	videoSpecs(user, dependentValues) {
		if (!dependentValues) { return false; }

		const { approval } = dependentValues;
		const isCorrectStatus = isProduction(approval) || isProductionIncomplete(approval);

		return (isDVAST(dependentValues.adType) || isCtvCreative(dependentValues)) && isCorrectStatus;
	},
	switchImposiumComposition(user, dependentValues) {
		if (!isImposiumCreative(dependentValues)) { return false; }

		return hasPermission(user, CV, CREATIVE_VIEW.UPLOAD_VAST);
	},
	editConceptContent(user, dependentValues){
		let permissionCheck = hasPermission(user, CV, CREATIVE_VIEW.UPLOAD_AD_FILES);

		if(!permissionCheck){return false;}
		if(!dependentValues){return permissionCheck;}

		let creativeCheck = isSocialCreative(dependentValues) && isConcept(dependentValues);

		return creativeCheck && permissionCheck;
	},
	renameConcept(user, dependentValues){
		let permissionCheck = hasPermission(user, CV, CREATIVE_VIEW.RENAME);

		if(!permissionCheck){return false;}
		if(!dependentValues){return permissionCheck;}

		let creativeCheck = isConcept(dependentValues);

		return creativeCheck && permissionCheck;
	},
	addSocialCreativeToConcept(user, dependentValues){
		let permissionCheck = hasPermission(user, CV, CREATIVE_VIEW.UPLOAD_AD_FILES);

		if(!permissionCheck){return false;}
		if(!dependentValues){return permissionCheck;}

		let creativeCheck = isSocialCreative(dependentValues);

		return creativeCheck && permissionCheck;
	},
	duplicateConcept(user, dependentValues){
		let permissionCheck = hasPermission(user, CV, CREATIVE_VIEW.DUPLICATE);

		if(!permissionCheck){return false;}
		if(!dependentValues){return permissionCheck;}

		let creativeCheck = isConcept(dependentValues);

		return creativeCheck && permissionCheck;
	},
	editSocialCreative(user){
		return hasPermission(user, CV, CREATIVE_VIEW.UPLOAD_AD_FILES);
	},
	openInAdStudio(user, dependentValues = {}){
		const { openInAdstudio } = dependentValues;

		return [
			hasPermission(user, CV, CREATIVE_VIEW.UPLOAD_AD_FILES),
			dependentValues.origin !== 'imposium',
			openInAdstudio,
		].every(item => item);
	},
	openInImposium(user, dependentValues = {}){
		return [
			hasPermission(user, CV, CREATIVE_VIEW.UPLOAD_AD_FILES),
			dependentValues.origin === 'imposium'
		].every(item => item);
	},
	addExistingCreativeToConcept(user, dependentValues) {
		let permissionCheck = hasPermission(user, CV, CREATIVE_VIEW.UPLOAD_AD_FILES);

		if(!permissionCheck){return false;}
		if(!dependentValues){return permissionCheck;}

		let creativeCheck = !isSocialCreative(dependentValues) && isConcept(dependentValues);

		return creativeCheck && permissionCheck;
	},
	newAdStudioCreativeToConcept(user, dependentValues) {
		let permissionCheck = hasPermission(user, CV, CREATIVE_VIEW.UPLOAD_AD_FILES);

		if(!permissionCheck){return false;}
		if(!dependentValues){return permissionCheck;}

		let creativeCheck = !isSocialCreative(dependentValues) && isConcept(dependentValues);

		return creativeCheck && permissionCheck;
	},
	newPartnerTagToConcept(user, dependentValues){
		if(!getPartnerTags()){ return false; }
		let permissionCheck = hasPermission(user, CV, CREATIVE_VIEW.UPLOAD_AD_FILES);

		if(!permissionCheck){return false;}
		if(!dependentValues){return permissionCheck;}

		let creativeCheck = !isSocialCreative(dependentValues) && isConcept(dependentValues);

		return creativeCheck && permissionCheck;
	},
	removeCreativeFromConcept(user, dependentValues) {
		if (!dependentValues) {
			return false;
		}

		const { approval } = dependentValues;
		const creativeCheck = isDisplayCreative(dependentValues) && isConcept(dependentValues) && !isAdstudioCreative(dependentValues);
		const stateCheck = !(isQA(approval) || isApproving(approval) || isApproved(approval));
		const permissionCheck = hasPermission(user, CV, CREATIVE_VIEW.UPLOAD_AD_FILES);

		return permissionCheck && creativeCheck && stateCheck;
	},
	previewVersions(dependentValues){
		const { approval } = dependentValues;

		return isDynamic(dependentValues) && !isProductionIncomplete(approval) && !isRejectedIncomplete(approval) && !isInvalidDVASTState(dependentValues);
	},
	uploadClosedCaption(user, dependentValues) {
		if (!dependentValues) {
			return false;
		}

		const creativeCheck = canHaveClosedCaption(dependentValues);
		const permissionCheck = hasPermission(user, CV, CREATIVE_VIEW.UPLOAD_AD_FILES);

		return creativeCheck && permissionCheck;
	},
	deleteClosedCaption(user, dependentValues) {
		if (!dependentValues) {
			return false;
		}

		const creativeHasClosedCaption = hasClosedCaption(dependentValues);
		const permissionCheck = hasPermission(user, CV, CREATIVE_VIEW.UPLOAD_AD_FILES);

		return creativeHasClosedCaption && permissionCheck;
	},
};


// Returns true as soon as it detects a creative that means it can't be deleted
export const canDeleteConcept = creativeArray => creativeArray.every(({ approvalValue }) => {
	return !isQA(approvalValue) && !isApproved(approvalValue);
});
